import angular from 'angular';
import { UPDATE_PROJECT } from 'services/Permissions';
import contactGroupExampleImage from '../../../assets/images/img_contact_group_example.jpg';

/* @ngInject */
export default class ContactsController {
    constructor($scope, $state, IAMService, ToastService, DialogService, ProjectManager, RoleManager) {
        this.$scope = $scope;
        this.$state = $state;
        this.IAMService = IAMService;
        this.ToastService = ToastService;
        this.DialogService = DialogService;
        this.ProjectManager = ProjectManager;
        this.RoleManager = RoleManager;
    }

    $onInit() {
        this.project = this.ProjectManager.currentProject;
        this.organizationId = this.project.organizationId;
        this.projectId = this.project.name.split('/')[1];
        
        // Contact Groups from organization
        this.contactGroups = [];
        this.loadingContactGroups = false;
        
        // Project contacts grouped by contact group
        this.contactsByGroup = new Map(); // contactGroupName -> contacts array
        this.loadingContactsForGroup = new Map(); // contactGroupName -> boolean
        
        // UI state per contact group
        this.expandedGroups = new Set(); // Which contact groups are expanded
        this.addingContactForGroup = null; // Which group we're adding to
        this.editingContact = null;
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
        
        // Contact group example image
        this.contactGroupExampleImage = contactGroupExampleImage;
        
        // Load contact groups first
        this.loadContactGroups();
    }

    async loadContactGroups() {
        this.loadingContactGroups = true;
        try {
            const response = await this.IAMService.listContactGroups(this.organizationId, this.projectId);
            this.contactGroups = response.contactGroups || [];
            
            // Initialize empty arrays for each contact group
            this.contactGroups.forEach(group => {
                this.contactsByGroup.set(group.name, []);
                this.loadingContactsForGroup.set(group.name, false);
            });
            
            // Load contacts for all groups immediately
            this.loadAllContacts();
            
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error loading contact groups:', error);
            this.contactGroups = [];
        } finally {
            this.loadingContactGroups = false;
            this.$scope.$applyAsync();
        }
    }

    async loadAllContacts() {
        // Load contacts for all contact groups in parallel
        const loadPromises = this.contactGroups.map(group => this.loadContactsForGroup(group));
        await Promise.all(loadPromises);
    }

    async loadContactsForGroup(contactGroup) {
        const groupName = contactGroup.name;
        
        if (this.loadingContactsForGroup.get(groupName)) {
            return; // Already loading
        }
        
        this.loadingContactsForGroup.set(groupName, true);
        try {
            const params = {
                contactGroup: groupName
            };
            const response = await this.IAMService.listContacts(this.projectId, params);
            const contacts = response.contacts || [];
            
            // Update contacts for this group
            this.contactsByGroup.set(groupName, contacts);
            
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error(`Error loading contacts for group ${groupName}:`, error);
            this.contactsByGroup.set(groupName, []);
        } finally {
            this.loadingContactsForGroup.set(groupName, false);
            this.$scope.$applyAsync();
        }
    }

    // Expansion methods
    toggleContactGroup(contactGroup) {
        if (this.expandedGroups.has(contactGroup.name)) {
            this.expandedGroups.delete(contactGroup.name);
            // Cancel any adding/editing when collapsing
            if (this.addingContactForGroup === contactGroup.name) {
                this.cancelAddingContact();
            }
        } else {
            this.expandedGroups.add(contactGroup.name);
            // Contacts are already loaded, no need to load again
        }
    }

    isContactGroupExpanded(contactGroupName) {
        return this.expandedGroups.has(contactGroupName);
    }

    isLoadingContactsForGroup(contactGroupName) {
        return this.loadingContactsForGroup.get(contactGroupName) || false;
    }

    startAddingContact(contactGroup) {
        this.addingContactForGroup = contactGroup.name;
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
        // Ensure the group is expanded
        this.expandedGroups.add(contactGroup.name);
    }

    cancelAddingContact() {
        this.addingContactForGroup = null;
        this.newContact = {
            displayName: '',
            email: '',
            phoneNumber: ''
        };
    }

    async addContact() {
        if (!this.newContact.displayName.trim()) {
            this.ToastService.showSimpleText('Please enter a contact name');
            return;
        }

        if (!this.newContact.email.trim() && !this.newContact.phoneNumber.trim()) {
            this.ToastService.showSimpleText('Please provide either an email or phone number');
            return;
        }

        if (!this.addingContactForGroup) {
            this.ToastService.showSimpleText('No contact group selected');
            return;
        }

        try {
            const contactData = {
                displayName: this.newContact.displayName.trim(),
                email: this.newContact.email.trim() || null,
                phoneNumber: this.newContact.phoneNumber.trim() || null,
                contactGroup: this.addingContactForGroup
            };

            const createdContact = await this.IAMService.createContact(this.projectId, contactData);
            
            // Add to the contact group
            if (this.contactsByGroup.has(this.addingContactForGroup)) {
                this.contactsByGroup.get(this.addingContactForGroup).push(createdContact);
            } else {
                this.contactsByGroup.set(this.addingContactForGroup, [createdContact]);
            }
            
            this.ToastService.showSimpleText('Contact added successfully');
            this.cancelAddingContact();
            this.$scope.$applyAsync();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error adding contact:', error);
            this.ToastService.showSimpleText(`Failed to add contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
    }

    startEditingContact(contact) {
        this.editingContact = {
            name: contact.name,
            originalContact: contact,
            newDisplayName: contact.displayName,
            newEmail: contact.email || '',
            newPhoneNumber: contact.phoneNumber || '',
            newContactGroup: contact.contactGroup
        };
    }

    cancelEditingContact() {
        this.editingContact = null;
    }

    async saveContact() {
        if (!this.editingContact.newDisplayName.trim()) {
            this.ToastService.showSimpleText('Please enter a contact name');
            return;
        }

        if (!this.editingContact.newEmail.trim() && !this.editingContact.newPhoneNumber.trim()) {
            this.ToastService.showSimpleText('Please provide either an email or phone number');
            return;
        }

        try {
            const contactId = this.editingContact.name.split('/').pop();
            const contactData = {
                displayName: this.editingContact.newDisplayName.trim(),
                email: this.editingContact.newEmail.trim() || null,
                phoneNumber: this.editingContact.newPhoneNumber.trim() || null,
                contactGroup: this.editingContact.newContactGroup
            };

            const updatedContact = await this.IAMService.updateContact(this.projectId, contactId, contactData);
            
            // Update the contact in the appropriate group
            const oldContactGroup = this.editingContact.originalContact.contactGroup;
            const newContactGroup = updatedContact.contactGroup;
            
            // Remove from old group
            if (this.contactsByGroup.has(oldContactGroup)) {
                const oldGroupContacts = this.contactsByGroup.get(oldContactGroup);
                const index = oldGroupContacts.findIndex(c => c.name === this.editingContact.name);
                if (index !== -1) {
                    oldGroupContacts.splice(index, 1);
                }
            }
            
            // Add to new group
            if (this.contactsByGroup.has(newContactGroup)) {
                this.contactsByGroup.get(newContactGroup).push(updatedContact);
            } else {
                this.contactsByGroup.set(newContactGroup, [updatedContact]);
            }
            
            this.ToastService.showSimpleText('Contact updated successfully');
            this.cancelEditingContact();
            this.$scope.$applyAsync();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error updating contact:', error);
            this.ToastService.showSimpleText(`Failed to update contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
    }

    async deleteContact(contact) {
        const confirm = await this.DialogService.confirm({
            title: 'Delete Contact',
            content: `Are you sure you want to delete "${contact.displayName}"?`,
            ok: 'Delete',
            cancel: 'Cancel'
        });

        if (!confirm) {
            return;
        }

        try {
            const contactId = contact.name.split('/').pop();
            await this.IAMService.deleteContact(this.projectId, contactId);
            
            // Remove from the contact group
            const contactGroup = contact.contactGroup;
            if (this.contactsByGroup.has(contactGroup)) {
                const groupContacts = this.contactsByGroup.get(contactGroup);
                const index = groupContacts.findIndex(c => c.name === contact.name);
                if (index !== -1) {
                    groupContacts.splice(index, 1);
                }
            }
            
            this.ToastService.showSimpleText('Contact deleted successfully');
            this.$scope.$applyAsync();
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error deleting contact:', error);
            this.ToastService.showSimpleText(`Failed to delete contact: ${error.data?.error || error.message || 'Unknown error'}`);
        }
    }

    getContactGroupDisplayName(contactGroupName) {
        const contactGroup = this.contactGroups.find(cg => cg.name === contactGroupName);
        return contactGroup ? contactGroup.displayName : contactGroupName;
    }

    getContactsForGroup(contactGroupName) {
        return this.contactsByGroup.get(contactGroupName) || [];
    }

    getTotalContactsCount() {
        let total = 0;
        // eslint-disable-next-line no-restricted-syntax
        for (const contacts of this.contactsByGroup.values()) {
            total += contacts.length;
        }
        return total;
    }

    isAddingContactForGroup(contactGroupName) {
        return this.addingContactForGroup === contactGroupName;
    }

    get canCreate() {
        return this.RoleManager.hasProjectPermissionTo(UPDATE_PROJECT);
    }

    get canEdit() {
        return this.RoleManager.hasProjectPermissionTo(UPDATE_PROJECT);
    }

    get canDelete() {
        return this.RoleManager.hasProjectPermissionTo(UPDATE_PROJECT, this.project);
    }

    showContactGroupsInfo() {
        this.DialogService.show({
            template: `
                <md-dialog class="contact-groups-info-dialog" style="max-width: 600px; width: 100%;">
                    <md-toolbar style="background-color: #f8f9fa; color: #15394c;">
                        <div class="md-toolbar-tools">
                            <div style="font-size: 16px; font-weight: 600;">What are Contact Groups?</div>
                            <span flex></span>
                            <md-button class="md-icon-button" ng-click="closeDialog()">
                                <md-icon class="text-color-icon" md-svg-icon="close"></md-icon>
                            </md-button>
                        </div>
                    </md-toolbar>
                    
                    <md-dialog-content style="padding: 24px;">
                        <p style="margin-bottom: 16px; font-size: 14px; line-height: 1.5;">
                            Contact groups are collections of people who should receive specific types of alerts. They help you organize your notification recipients and make it easy to send alerts to the right people at the right time.
                        </p>
                        
                        <div style="margin-bottom: 16px; text-align: center;">
                            <img ng-src="{{contactGroupExampleImage}}" 
                                 alt="Contact Group Example" 
                                 style="max-width: 80%; height: auto; border-radius: 8px; border: 1px solid #4a727f2e;">
                        </div>
                        
                        <div style="margin-bottom: 16px;">
                            <h4 style="margin: 0 0 8px 0; font-size: 14px; font-weight: 600;">Examples of contact groups:</h4>
                            <ul style="margin: 0; padding-left: 20px; font-size: 14px; line-height: 1.6;">
                                <li style="margin-bottom: 4px;"><strong>Store Employees</strong> - Front-line staff who need operational alerts</li>
                                <li style="margin-bottom: 4px;"><strong>District Managers</strong> - Regional management for escalated issues</li>
                                <li style="margin-bottom: 4px;"><strong>Maintenance Team</strong> - Technical staff for equipment-related alerts</li>
                                <li style="margin-bottom: 4px;"><strong>Security Personnel</strong> - Staff responsible for security incidents</li>
                            </ul>
                        </div>
                        
                        <p style="margin: 0; font-size: 14px; line-height: 1.5;">
                            By organizing contacts into groups, you can quickly assign different alert templates to send notifications to the appropriate teams based on the type of alert.
                        </p>
                    </md-dialog-content>
                    
                    <md-dialog-actions layout="row">
                        <span flex></span>
                        <md-button ng-click="closeDialog()" class="md-primary">
                            Got it
                        </md-button>
                    </md-dialog-actions>
                </md-dialog>
            `,
            controller: ['$scope', 'DialogService', 'contactGroupExampleImage', function($scope, DialogService, exampleImage) {
                $scope.closeDialog = function() {
                    DialogService.cancel();
                };
                $scope.contactGroupExampleImage = exampleImage;
            }],
            locals: {
                contactGroupExampleImage: this.contactGroupExampleImage
            },
            parent: angular.element(document.body),
            clickOutsideToClose: true,
            escapeToClose: true
        });
    }
} 